import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { ThemeProvider, createTheme } from "@mui/material";
import { toast } from "react-toastify";
import api from "../../config/URL";

/* ================= CSV EXPORT HELPER ================= */
const exportVisibleColumnsToCSV = (
  table,
  rows,
  fileName = "Referral_Report.csv",
) => {
  const visibleColumns = table
    .getAllLeafColumns()
    .filter((col) => col.getIsVisible() && col.columnDef.accessorKey);

  const headers = visibleColumns.map((col) => col.columnDef.header);

  const csvRows = rows.map((row) =>
    visibleColumns.map((col) => {
      const value = row[col.columnDef.accessorKey];
      return `"${value ?? ""}"`;
    }),
  );

  const csvContent = [
    headers.join(","),
    ...csvRows.map((r) => r.join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], {
    type: "text/csv;charset=utf-8;",
  });

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
};
/* ==================================================== */

const ReferalReports = ({ selectedCenter }) => {
  const [filters, setFilters] = useState({
    centerId: selectedCenter || "",
    studentName: "",
    referrerParent: "",
    status: "",
    amount: "",
  });

  const [studentsData, setStudentData] = useState([]);
  const [referralSummary, setReferralSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fetchStudentReportsData = async (filterValues = filters) => {
    if (!selectedCenter) return;
    setLoading(true);

    try {
      const params = new URLSearchParams();
      params.append("centerId", selectedCenter);

      if (filterValues.studentName)
        params.append("studentName", filterValues.studentName);
      if (filterValues.status) params.append("status", filterValues.status);
      if (filterValues.referrerParent)
        params.append("referrerParent", filterValues.referrerParent);
      if (filterValues.amount)
        params.append("referralFee", filterValues.amount);

      const response = await api.get(`getReferralReport?${params.toString()}`);
      setStudentData(response.data?.records || []);
      setReferralSummary(response.data?.summary || null);
    } catch (error) {
      toast.error(error.message || "Error fetching referral report");
      setStudentData([]);
      setReferralSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorFn: (row, index) => index + 1,
        id: "__row_number",
        header: "S.NO",
        size: 40,
      },
      {
        accessorKey: "studentName",
        header: "Student Name",
      },
      {
        accessorKey: "referrerParent",
        header: "Parent Name",
      },
      {
        accessorKey: "referrer",
        header: "Referrer",
      },
      {
        accessorKey: "creditNote",
        header: "CN Code",
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "payoutStatus",
        header: "Status",
      },
      {
        accessorKey: "referralFee",
        header: "Amount",
        Cell: ({ cell }) => cell.getValue()?.toLocaleString() || "0",
      },
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data: studentsData,
    enableColumnActions: true,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () =>
        navigate(`/student/view/${row.original.studentId}`, {
          state: { backUrl: "/report/referalRepots" },
        }),
      style: { cursor: "pointer" },
    }),
    /* -------- EXPORT BUTTON ADDED -------- */
    renderTopToolbarCustomActions: ({ table }) => (
      <button
        className="btn btn-sm btn-border"
        onClick={() =>
          exportVisibleColumnsToCSV(table, studentsData, "Referral_Report.csv")
        }
      >
        Export CSV
      </button>
    ),
  });

  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454 !important",
            backgroundColor: "#e6edf7 !important",
            fontWeight: "400 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
    },
  });
  // Handle filter changes
  const handleFilterChange = (eOrName, value) => {
    if (typeof eOrName === "string") {
      setFilters((prev) => ({ ...prev, [eOrName]: value }));
    } else {
      const { name, value } = eOrName.target;
      setFilters((prev) => ({ ...prev, [name]: value }));
    }
  };

  const applyFilters = () => {
    fetchStudentReportsData(filters);
  };

  const clearFilter = () => {
    const resetFilters = {
      centerId: selectedCenter || "",
      studentName: "",
      status: "",
      referrerParent: "",
      amount: "",
    };
    setFilters(resetFilters);
    fetchStudentReportsData(resetFilters);
  };

  useEffect(() => {
    if (selectedCenter) {
      setFilters((prev) => ({
        ...prev,
        centerId: selectedCenter,
      }));
      fetchStudentReportsData({
        ...filters,
        centerId: selectedCenter,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCenter]);

  return (
    <div className="container-fluid my-4 center">
      <ol
        className="breadcrumb my-3 px-2"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          Referral Report<span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          Referral Report
        </li>
      </ol>
      <div className="card">
        <div
          className="mb-3 d-flex justify-content-between align-items-center p-1"
          style={{ background: "#f5f7f9" }}
        >
          <div className="d-flex align-items-center">
            <div className="d-flex">
              <div className="dot active"></div>
            </div>
            <span className="me-2 text-muted">
              This database shows the list of{" "}
              <span className="bold" style={{ color: "#287f71" }}>
                Referral Report
              </span>
            </span>
          </div>
        </div>

        {/* Referral Summary Display */}
        {referralSummary && (
          <div className="mb-3 px-3">
            <h6>Referral Summary</h6>
            <div className="d-flex flex-wrap gap-3">
              <span>
                Total Referrals:{" "}
                <strong>{referralSummary.totalReferrals}</strong>
              </span>
              <span>
                Unpaid Referrals:{" "}
                <strong>{referralSummary.unpaidReferrals}</strong>
              </span>
            </div>
          </div>
        )}

        <div className="mb-3">
          <div className="individual_fliters d-lg-flex ">
            <div className="form-group mb-0 ms-2 mb-1">
              <input
                type="text"
                name="studentName"
                value={filters.studentName}
                onChange={handleFilterChange}
                className="form-control form-control-sm center_list"
                placeholder="Student Name"
                autoComplete="off"
              />
            </div>
            <div className="form-group mb-0 ms-2 mb-1">
              <input
                type="text"
                name="referrerParent"
                value={filters.referrerParent}
                onChange={handleFilterChange}
                className="form-control form-control-sm center_list"
                placeholder="Parent Name"
                autoComplete="off"
              />
            </div>
            <div className="form-group mb-0 ms-2 mb-1">
              <select
                className="form-select form-select-sm center_list"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">Select Status</option>
                <option value="Used">Used</option>
                <option value="Unused">Unused</option>
              </select>
            </div>
            <div className="form-group mb-0 ms-2 mb-1">
              <button
                type="button"
                style={{ background: "#eb862a" }}
                className="btn btn-sm text-white"
                onClick={applyFilters}
              >
                Search
              </button>
            </div>
            <div className="form-group mb-0 ms-2 mb-1">
              <button
                type="button"
                className="btn btn-sm btn-border"
                onClick={clearFilter}
              >
                Clear
              </button>
            </div>
          </div>
          <div className="d-flex justify-content-between">
            <div className="individual_fliters d-lg-flex mt-2">
              {/* <div className="form-group mb-0 ms-2 mb-1">
                <input
                  type="text"
                  name="amount"
                  value={filters.amount}
                  onChange={handleFilterChange}
                  className="form-control form-control-sm center_list"
                  placeholder="Amount"
                  autoComplete="off"
                />
              </div> */}
            </div>
          </div>
        </div>
        {loading ? (
          <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <ThemeProvider theme={theme}>
            <MaterialReactTable table={table} />
          </ThemeProvider>
        )}
      </div>
    </div>
  );
};

export default ReferalReports;
